import React, { useEffect, useState } from 'react';
import { BadgeCheck, MapPin, Clock } from 'lucide-react';

// Live-updating clock hook
export function useNow(interval = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(id);
  }, [interval]);
  return now;
}

export function formatLeft(target, now = Date.now()) {
  const diff = target - now;
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h >= 1) return `${h}h left`;
  if (m >= 1) return `${m}m left`;
  return `${s}s left`;
}

export function formatExpiring(target, now = Date.now()) {
  const diff = target - now;
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 1) return `Expiring in ${h}h`;
  return `Expiring in ${m}m`;
}

// Circular avatar with optional online dot / story ring
export function Avatar({ src, alt = '', size = 44, online, ring, className = '' }) {
  const dim = { width: size, height: size };
  const inner = (
    <img
      src={src}
      alt={alt}
      style={dim}
      className="rounded-full object-cover bg-[#141a24]"
      loading="lazy"
    />
  );
  return (
    <div className={`relative shrink-0 ${className}`} style={dim}>
      {ring ? (
        <div className="story-ring rounded-full p-[2px]" style={dim}>
          <div className="bg-[#05070c] rounded-full p-[2px] w-full h-full">
            <img src={src} alt={alt} className="w-full h-full rounded-full object-cover" loading="lazy" />
          </div>
        </div>
      ) : (
        inner
      )}
      {online && (
        <span className="absolute bottom-0 right-0 block w-3 h-3 rounded-full bg-[#22c55e] ring-2 ring-[#0a0e17]" />
      )}
    </div>
  );
}

export function Verified({ size = 15, className = '' }) {
  return <BadgeCheck size={size} className={`text-[#22c55e] shrink-0 ${className}`} strokeWidth={2.4} />;
}

export function LocationDot({ size = 14 }) {
  return <MapPin size={size} className="text-[#2f8bff] shrink-0" />;
}

// Time-left pill — turns amber when < 6h remain
export function TimeLeftPill({ target, now, prefix }) {
  const diff = target - (now || Date.now());
  const urgent = diff <= 6 * 3600000;
  const label = prefix ? formatExpiring(target, now) : formatLeft(target, now);
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-[3px] rounded-full ${
        urgent
          ? 'bg-[#3a2a12] text-[#f5a524]'
          : 'bg-[#141c28] text-[#8b95a5]'
      }`}
    >
      <Clock size={11} strokeWidth={2.4} />
      {label}
    </span>
  );
}


// Renders @mentions and #hashtags as blue tokens
export function RichText({ text, className = '' }) {
  const parts = (text || '').split(/(\s+)/);
  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (/^@[\w.]+/.test(p)) return <span key={`m${i}`} className="text-[#2f8bff] font-medium">{p}</span>;
        if (/^#[\w]+/.test(p)) return <span key={`h${i}`} className="text-[#2f8bff]">{p}</span>;
        return <React.Fragment key={`t${i}`}>{p}</React.Fragment>;
      })}
    </span>
  );
}
